import { LINE_ITEM_KINDS, lineItemKind } from "~/constants/line_item_kinds"
import IInvoice from "../interfaces/IInvoice"
import ILineItem from "../interfaces/ILineItem"
import IOrder from "../interfaces/IOrder"
import IOrderPreview from "../interfaces/IOrderPreview"
import IPayment from "../interfaces/IPayment"
import IPaymentLink from "../interfaces/IPaymentLink"
import { get, patch, post, transformRequestData } from "./utils/requests"
import { ElectricPayload, ElectricResponse } from "~/interfaces/ElectricPrices"

const ORDERS_API = "/api/orders/"

export enum serviceRateMode {
  pos = "pos",
  payment_link = "payment_link",
  base = "base",
}

export const mapLineItemToParams = (li: Partial<ILineItem>, isTaxFeeEnabled: boolean): LineItemParams => ({
  kind: li.kind,
  label: li.label,
  total_amount_in_cents: li.reservation_id ? li.total_amount_in_cents : null,
  unit_amount_in_cents: li.reservation_id
    ? null
    : li.unit_amount_in_cents !== undefined && li.unit_amount_in_cents !== null
      ? li.unit_amount_in_cents
      : li.total_amount_in_cents && li.quantity && li.occurrences
        ? li.total_amount_in_cents / (Number(li.quantity) * Number(li.occurrences))
        : li.total_amount_in_cents && li.quantity
          ? li.total_amount_in_cents / Number(li.quantity)
          : null,
  quantity: li.quantity,
  occurrences: li.occurrences,
  occurrence_unit: li.occurrence_unit,
  is_tax_fee_enabled: isTaxFeeEnabled,
  reservation_id: li.reservation_id,
  add_on_id: li.add_on_id,
  is_attached_to_reservation: !!li.parent_line_item_id,
})

export const partialLineItemParams = (
  amount: number,
  isTaxFeeEnabled: boolean,
  reservation_id?: string,
  isDeposit: boolean = false,
): Array<LineItemParams> => [
  {
    kind: isDeposit ? LINE_ITEM_KINDS.DEPOSIT : LINE_ITEM_KINDS.PARTIAL_PAYMENT,
    label: isDeposit ? "Deposit" : "Partial Payment",
    unit_amount_in_cents: amount,
    quantity: 1,
    is_tax_fee_enabled: isDeposit ? false : isTaxFeeEnabled,
    total_amount_in_cents: null,
    occurrences: null,
    occurrence_unit: null,
    reservation_id: null,
    add_on_id: null,
    is_attached_to_reservation: false,
  },
  {
    kind: LINE_ITEM_KINDS.BASIC,
    label: "Reservation",
    unit_amount_in_cents: 0,
    quantity: 1,
    is_tax_fee_enabled: false,
    total_amount_in_cents: null,
    occurrences: null,
    occurrence_unit: null,
    add_on_id: null,
    is_attached_to_reservation: false,
    reservation_id: reservation_id,
  },
]

/** @deprecated */
export const depositLineItemParams = (
  amount: number,
  isTaxFeeEnabled: boolean,
  reservation_id?: string,
): Array<LineItemParams> => [
  {
    kind: LINE_ITEM_KINDS.DEPOSIT,
    label: "Deposit",
    unit_amount_in_cents: amount,
    quantity: 1,
    is_tax_fee_enabled: isTaxFeeEnabled,
    total_amount_in_cents: null,
    occurrences: null,
    occurrence_unit: null,
    reservation_id: null,
    add_on_id: null,
    is_attached_to_reservation: false,
  },
  {
    kind: LINE_ITEM_KINDS.BASIC,
    label: "Reservation",
    unit_amount_in_cents: 0,
    quantity: 1,
    is_tax_fee_enabled: false,
    total_amount_in_cents: null,
    occurrences: null,
    occurrence_unit: null,
    add_on_id: null,
    is_attached_to_reservation: false,
    reservation_id: reservation_id,
  },
]

export type OrderParams = {
  order_id: string | undefined
  camp_id: string
  camping_style_id: string | undefined
  camper_id?: string
  is_credit_card_fee_enabled: boolean
  discount_slug: string
  is_storing_credit_card_enabled?: boolean
  is_auto_charging_enabled?: boolean
  is_terminal_payment?: boolean
  selected_payment_method_id?: string
  is_store?: boolean
  line_items_attributes: Array<LineItemParams>
  partial_line_items_attributes?: Array<LineItemParams>
  is_skipping_reservation_status_update?: boolean
}

export type OrderPreviewParams = OrderParams & {
  order_id: string | undefined
  is_service_fee_enabled: boolean
  service_rate_mode: serviceRateMode
  include_payment_methods?: boolean
  include_invoice?: boolean
  is_partial_payment?: boolean
  include_payments?: boolean
  selected_payment_method?: string
}

export type LineItemParams = {
  kind: lineItemKind
  label: string
  total_amount_in_cents: number | null
  unit_amount_in_cents: number | null
  occurrences: number
  occurrence_unit: string
  quantity: number
  is_tax_fee_enabled: boolean
  reservation_id: string | null
  add_on_id: string | null
  is_attached_to_reservation: boolean
}

type PaymentLinkParams = {
  email: string
}

const fetch = async (
  id: string,
  includePaymentMethods?: boolean,
  options?: {
    includeInvoices?: boolean
    includePayments?: boolean
  },
): Promise<{ order: IOrder }> => get(`/api/orders/${id}`, transformRequestData({ ...options, includePaymentMethods }))

const orderAndPreviewFor = async (
  id: string,
  campId: string,
  campingStyleId: string,
  mode: serviceRateMode = serviceRateMode.base,
  isPaid: boolean = false,
  discountSlug: string,
  includePaymentMethods?: boolean,
  options?: {
    includeInvoice?: boolean
    includePayments?: boolean
  },
): Promise<{ order: IOrder; preview?: IOrderPreview; invoice?: IInvoice }> => {
  const { order } = await fetch(id, false, options)
  let preview: IOrderPreview | undefined
  let invoice: IInvoice | undefined

  if (!isPaid) {
    const { order: orderWithPreview } = await createPreview({
      order_id: order.id,
      camp_id: campId,
      camping_style_id: campingStyleId,
      is_credit_card_fee_enabled: !!order.credit_card_rate,
      is_service_fee_enabled: true,
      service_rate_mode: mode,
      discount_slug: discountSlug,
      line_items_attributes: order.line_items.map((li) => mapLineItemToParams(li, Number(li.tax_rate) > 0)),
      include_payment_methods: includePaymentMethods,
      include_invoice: options?.includeInvoice,
      include_payments: options?.includePayments,
    })

    preview = orderWithPreview.preview
    invoice = orderWithPreview.invoice
  }

  return Promise.resolve({ order, preview, invoice })
}

const orderAndPreviewForNew = async (
  order: IOrder,
  campId: string,
  campingStyleId: string,
  mode: serviceRateMode = serviceRateMode.base,
  isPaid: boolean = false,
  discountSlug: string,
  includePaymentMethods?: boolean,
  options?: {
    includeInvoice?: boolean
    includePayments?: boolean
  },
): Promise<{ order: IOrder; preview?: IOrderPreview; invoice?: IInvoice }> => {
  let preview: IOrderPreview | undefined
  let invoice: IInvoice | undefined

  if (!isPaid) {
    const { order: orderWithPreview } = await createPreview({
      order_id: order.id,
      camp_id: campId,
      camping_style_id: campingStyleId,
      is_credit_card_fee_enabled: !!order.credit_card_rate,
      is_service_fee_enabled: true,
      service_rate_mode: mode,
      discount_slug: discountSlug,
      line_items_attributes: order.line_items.map((li) => mapLineItemToParams(li, Number(li.tax_rate) > 0)),
      include_payment_methods: includePaymentMethods,
      include_invoice: options?.includeInvoice,
      include_payments: options?.includePayments,
    })

    preview = orderWithPreview.preview
    invoice = orderWithPreview.invoice
  }

  return Promise.resolve({ order, preview, invoice })
}

const createPreview = async (
  data: OrderPreviewParams,
): Promise<{ order: { preview: IOrderPreview } & { invoice?: IInvoice } }> => post(`${ORDERS_API}previews`, data)

const createPayment = async (
  data: OrderParams,
  options?: {
    isForGroup?: boolean
  },
): Promise<{ payment: IPayment & { order: IOrder } }> =>
  options?.isForGroup ? post(`${ORDERS_API}groups/payments`, data) : post(`${ORDERS_API}payments`, data)

const createPaymentIntent = async (
  data: OrderParams,
): Promise<{ payment_intent: { id: string; client_secret: string; status: string; order: IOrder } }> =>
  post(`${ORDERS_API}payment_intents`, data)

const updateOrderCamper = async (
  orderId: string,
  camperId: string,
): Promise<{ payment_link: IPaymentLink & { order: IOrder } }> =>
  patch(`${ORDERS_API}${orderId}`, { camper_id: camperId })

const createPaymentLink = async (
  data: OrderParams & PaymentLinkParams,
): Promise<{ payment_link: IPaymentLink & { order: IOrder } }> => post(`${ORDERS_API}payment_links`, data)

const createPreviewElectricPrices = async (data: ElectricPayload): Promise<ElectricResponse> =>
  post(`${ORDERS_API}previews/electric_prices`, data)

const Orders = {
  createPayment,
  createPaymentIntent,
  createPaymentLink,
  createPreview,
  fetch,
  orderAndPreviewFor,
  updateOrderCamper,
  createPreviewElectricPrices,
  orderAndPreviewForNew,
}

export default Orders
